import {
  useState,
  useId,
  useRef,
  useLayoutEffect,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type MutableRefObject,
  type MouseEventHandler,
} from "react";
import cx from "classnames";

import { defaultItemToString } from "../../Combobox/ComboboxUtils";
import { Flex } from "../../Flex";
import { Label, LabelProps } from "../../Label";
import { TextFieldProps } from "../../TextField";
import { Button } from "../../Button";
import { Chip, ChipProps } from "../../Chip";
import { Helper } from "../../../internal/components";

import styles from "./SelectTriggerBase.module.scss";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import Chevron_Right from "@servicetitan/hammer-icon/mdi/round/chevron_right.svg";

type SelectTriggerBaseProps<Item = any> = Omit<
  TextFieldProps,
  "showCounter"
> & {
  /**
   * Should be a positive integer.
   */
  maxRows?: number;

  /** Allows Combobox/Select to display custom-colored Chips */
  selectedItemProps?: (item: Item, index: number) => Partial<ChipProps>;
} & {
  itemToString?: (item: any) => string;
  removeSelectedItem?: (item: Item) => void;
  disableClearSelection?: boolean;
  selectedItem: Item | null;
  selectedItems: Item[];

  inputValue?: string;
  referenceRef?: MutableRefObject<HTMLDivElement | null>;
} & {
  variant?: "select" | "combobox";
  onClearButtonClick?: MouseEventHandler<HTMLButtonElement>;
  labelProps?: Partial<LabelProps>;
  inputWrapperProps?: ComponentPropsWithoutRef<"div">;
  toggleButtonProps?: ComponentPropsWithoutRef<"button">;
  chipProps?: (item: Item, index: number) => Partial<ChipProps>;
  inputProps?: ComponentPropsWithoutRef<"input" | "div">;
};

export const SelectTriggerBase = function <Item>({
  className,
  label,
  size,
  error,
  hint,
  description,
  prefix,
  suffix,
  maxRows,
  selectedItemProps = () => ({}),
  disabled,
  readOnly,
  /** ======== */
  disableClearSelection,
  itemToString: itemToStringProp,
  selectedItem,
  selectedItems,
  removeSelectedItem,
  inputValue,
  referenceRef,
  /** ======== */
  variant = "select",
  onClearButtonClick,
  labelProps,
  inputWrapperProps = {},
  toggleButtonProps = {},
  inputProps = {},
  chipProps = (_item: Item, _index: number) => ({}),
  placeholder,
  ...rest
}: SelectTriggerBaseProps<Item>) {
  const itemToString = itemToStringProp ?? defaultItemToString<Item>;

  const ComboboxTriggerClassNames = cx(styles["search-field"], {
    [styles["select"]]: variant === "select",
  });

  const noClearButton = disabled || readOnly || disableClearSelection;

  const ComboboxInputWrapperClassNames = cx(
    styles["input-wrapper"],
    className
      ? {
          [className]: variant === "select",
        }
      : {},
    {
      [styles["small"]]: size === "small",
      [styles["large"]]: size === "large",
      [styles["no-clear-button"]]: noClearButton,
    },
  );

  const ComboboxInputClassNames = cx(
    styles["input"],
    className
      ? {
          [className]: variant === "combobox",
        }
      : {},
    {
      [styles["error"]]: error,
    },
  );

  const helperUid = "helper" + useId();
  const placeholderUid = "placeholder" + useId();
  const ariaDescribedBy = `${helperUid} ${placeholderUid}`;

  const errorMessage = typeof error !== "boolean" ? error : undefined;

  const rowsRef = useRef<HTMLDivElement>(null);
  const [stillCalculating, setStillCalculating] = useState<boolean>(false);
  const [collapsedChips, setCollapsedChips] = useState<boolean>(false);
  const [visibleChipsCount, setVisibleChipsCount] = useState<number | null>(
    null,
  );
  const [forceRenderCount, setForceRenderCount] = useState<number>(0);

  useLayoutEffect(() => {
    // we only need to do this nonsense if maxRows is set
    if (maxRows === undefined) return;

    // 1) Render with no chips collapsed, if it's already just one row, we're done
    // 2) If there is a second row, then render again with best guess at how we should collapse
    // 3) If there is still a second row while collapsed, then render again with one less item
    // 4) Keep going until there is only one row, then /actually/ render

    // start calculation loop, set intiial values and force re-render
    if (stillCalculating === false) {
      setStillCalculating(true);
      setCollapsedChips(false);
      setVisibleChipsCount(null);
      setForceRenderCount((x) => x + 1);

      // otherwise, calculate the number of rows
    } else if (rowsRef?.current?.children) {
      const children = rowsRef?.current?.children;

      const rowData = Array.from(children).reduce(
        (acc, child) => {
          const top = child.getBoundingClientRect().top;

          if (!acc.length) {
            return [{ count: 1, top }];
          } else if (acc[acc.length - 1].top === top) {
            return [
              ...acc.slice(0, -1),
              { count: acc[acc.length - 1].count + 1, top },
            ];
          } else if (acc[acc.length - 1].top !== top) {
            return [...acc, { count: 1, top }];
          } else {
            return acc;
          }
        },
        [] as { count: number; top: number }[],
      );

      // Call it a day if:
      // We don't have a second row OR
      // We're already hiding everything OR
      // There's only one item selected
      if (
        !rowData[maxRows] ||
        visibleChipsCount === 0 ||
        selectedItems.length === 1
      ) {
        setStillCalculating(false);
      } else {
        setCollapsedChips(true);

        // otherwise, either guess how many visible chips we have
        if (visibleChipsCount == null) {
          const firstGuess = Math.max(
            rowData.reduce((sum, row) => sum + row.count, -2),
            0,
          );
          setVisibleChipsCount(firstGuess);

          // or decrease our last guess by 1 and try again
        } else {
          setVisibleChipsCount(visibleChipsCount - 1);
        }

        setForceRenderCount((x) => x + 1);
      }
    }
  }, [selectedItems.length, maxRows, forceRenderCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedItemsDisplayCount =
    collapsedChips && maxRows != null && visibleChipsCount !== null
      ? visibleChipsCount
      : selectedItems.length;

  // TODO: Update this to use a TextField component once we can pass props
  //       directly to both the label and the text input
  return (
    <div {...rest} className={ComboboxTriggerClassNames} ref={referenceRef}>
      {label ? (
        <Label
          {...labelProps}
          className={cx(styles["label"], labelProps?.className)}
        >
          {label}
        </Label>
      ) : null}

      <div
        {...inputWrapperProps}
        className={ComboboxInputWrapperClassNames}
        {...(variant === "select"
          ? {
              "aria-describedby": ariaDescribedBy,
              "aria-labelledby": labelProps?.id,
            }
          : {})}
      >
        <div className={styles["buttons-wrapper"]}>
          {(inputValue || selectedItem || selectedItems.length) &&
          !noClearButton ? (
            <div className={styles["close-button-wrapper"]}>
              <Button
                aria-label="clear selection"
                appearance="ghost"
                size="small"
                icon={Close}
                className={styles["close-button"]}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  onClearButtonClick?.(e);
                }}
                tabIndex={-1}
              />
            </div>
          ) : null}

          <div className={styles["toggle-button-wrapper"]}>
            <Button
              {...toggleButtonProps}
              {...(variant === "select" ? { tabIndex: -1, inert: "true" } : {})}
              className={styles["toggle-button"]}
              aria-label="toggle menu"
              icon={Chevron_Right}
              appearance="ghost"
              size="small"
              disabled={disabled}
            />
          </div>
        </div>

        <div className={styles["prefix-wrapper"]}>
          {prefix ? <div className={styles["prefix"]}>{prefix}</div> : null}
        </div>

        <div className={styles["rows-wrapper"]} ref={rowsRef}>
          {selectedItems.length && removeSelectedItem != null
            ? selectedItems
                .slice(0, selectedItemsDisplayCount)
                .map((item, index) => {
                  return (
                    <div
                      key={`selected-item-${index}`}
                      className={styles["chip-wrapper"]}
                    >
                      <Chip
                        {...chipProps(item, index)}
                        label={itemToString(item)}
                        onClose={
                          disabled || readOnly
                            ? undefined
                            : (_e) => {
                                removeSelectedItem(item);
                              }
                        }
                        className={styles["chip"]}
                        title={itemToString(item)}
                        {...selectedItemProps(item, index)}
                      />
                    </div>
                  );
                })
            : null}

          {collapsedChips && maxRows != null ? (
            <div className={styles["chip-wrapper"]}>
              <Chip
                label={`+${selectedItems.length - selectedItemsDisplayCount}`}
                className={styles["chip"]}
                title={selectedItems
                  .slice(selectedItemsDisplayCount)
                  .map((item) => itemToString(item))
                  .join(", ")}
              />
            </div>
          ) : null}

          <Flex className={styles["input-flex"]} alignItems="center">
            {variant === "select" ? (
              <div
                {...inputProps}
                className={cx(ComboboxInputClassNames, inputProps.className)}
              >
                {selectedItems.length ? null : selectedItem ? (
                  itemToString(selectedItem)
                ) : (
                  <span
                    className={styles["fake-placeholder"]}
                    id={placeholderUid}
                  >
                    {placeholder}
                  </span>
                )}
              </div>
            ) : (
              <input
                {...inputProps}
                placeholder={placeholder}
                className={cx(ComboboxInputClassNames, inputProps.className)}
                {...(inputProps["aria-expanded"] != null &&
                inputProps["aria-controls"] != null
                  ? {
                      role: "combobox",
                    }
                  : {})}
              />
            )}

            {suffix ? <div className={styles["suffix"]}>{suffix}</div> : null}
          </Flex>
        </div>
      </div>

      {hint || errorMessage || description ? (
        <Helper
          id={helperUid}
          errorMessage={errorMessage}
          hint={hint}
          description={description}
        />
      ) : null}
    </div>
  );
};
